import { DOCUMENT } from '@angular/common';
import { ErrorHandler, inject, Injectable, Injector } from '@angular/core';
import { LoggerService } from '@rms/core/logger/logger.service';
import { NotificationService } from '@rms/notification';
import { AppError } from '@rms/sdk/errors';

import { isChunkLoadError, promptReloadForFreshChunks } from './chunk-load-recovery';

const DUPLICATE_NOTIFICATION_WINDOW_MS = 4000;

@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  private readonly injector = inject(Injector);
  private readonly document = inject(DOCUMENT);

  private lastNotifiedKey: string | null = null;
  private lastNotifiedAt = 0;

  public handleError(error: unknown): void {
    const original = this.unwrap(error);
    const logger = this.injector.get(LoggerService);

    // Safety net for dynamic-import failures that reach the global handler (lazy
    // route loads are handled earlier by the router navigation-error handler).
    // Prompt the user to reload for fresh bundles instead of reloading silently.
    if (isChunkLoadError(original)) {
      logger.warn('Lazy chunk failed to load; prompting user to reload', 'GlobalErrorHandler.chunkReload');
      promptReloadForFreshChunks(this.injector.get(NotificationService), this.document.defaultView);

      return;
    }

    if (this.isImageLoadError(original)) return;

    if (original instanceof AppError) {
      logger.error(original.source, `Unhandled AppError: ${original.name}`);
      this.notify(original.translationKey);

      return;
    }

    logger.error(original, 'Unhandled error');

    // Framework-internal errors (NG0xxx) aren't actionable for the user; log only.
    if (this.isAngularRuntimeError(original)) return;

    this.notify('common.errors.unexpected');
  }

  // Angular's RuntimeError is private API (ɵRuntimeError); detect it by its numeric code and NG0xxx message prefix.
  private isAngularRuntimeError(error: unknown): boolean {
    return (
      error instanceof Error &&
      typeof (error as Error & { code?: unknown }).code === 'number' &&
      /^NG0\d+/.test(error.message)
    );
  }

  private isImageLoadError(error: unknown): boolean {
    if (!(error instanceof Error)) {
      return false;
    }

    return /assets\/img\/icons/i.test(error.message ?? '');
  }

  private notify(translationKey: string): void {
    const now = Date.now();

    if (translationKey === this.lastNotifiedKey && now - this.lastNotifiedAt < DUPLICATE_NOTIFICATION_WINDOW_MS) {
      return;
    }

    this.lastNotifiedKey = translationKey;
    this.lastNotifiedAt = now;
    this.injector.get(NotificationService).error(translationKey);
  }

  // Angular wraps zone and unhandled-rejection errors; reach the original before the instanceof check.
  private unwrap(error: unknown): unknown {
    if (typeof error === 'object' && error !== null) {
      if ('rejection' in error) {
        return error.rejection;
      }

      if ('ngOriginalError' in error) {
        return error.ngOriginalError;
      }
    }

    return error;
  }
}
